import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, AlertTriangle, Wallet, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { bookingApi, settingsApi } from '../../lib/api';

const fmt = (v) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
        .format(Number(v || 0));

/**
 * Storno-Dialog für Mitglieder. Lädt Quote (Gebühr / Erstattung) live vom Backend
 * und reicht die Anfrage zur Genehmigung an die Geschäftsstelle weiter.
 */
export default function CancellationRequestDialog({ booking, open, onOpenChange, onSubmitted }) {
    const [quote, setQuote] = useState(null);
    const [reasons, setReasons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        reason_key: '', reason_text: '',
        iban: '', bic: '', account_holder: '',
        confirm: false,
    });

    useEffect(() => {
        if (!open || !booking) return;
        setLoading(true);
        Promise.all([
            bookingApi.getCancellationQuote(booking.id),
            settingsApi.getCancellationReasons(),
        ])
            .then(([q, r]) => {
                setQuote(q.data);
                setReasons(r.data.reasons || []);
            })
            .catch((e) => toast.error(e.response?.data?.detail || 'Storno-Daten konnten nicht geladen werden'))
            .finally(() => setLoading(false));
    }, [open, booking]);

    const submit = async () => {
        if (!form.reason_key && !form.reason_text.trim()) {
            toast.error('Bitte einen Storno-Grund wählen oder im Freitextfeld angeben.');
            return;
        }
        if (quote?.needs_bank_details) {
            if (!form.iban.trim() || !form.account_holder.trim()) {
                toast.error('Für die Erstattung benötigen wir IBAN und Kontoinhaber.');
                return;
            }
        }
        if (!form.confirm) {
            toast.error('Bitte die Storno-Bedingungen bestätigen.');
            return;
        }
        setSubmitting(true);
        try {
            await bookingApi.requestCancellation(booking.id, {
                reason_key: form.reason_key || null,
                reason_text: form.reason_text.trim() || null,
                iban: form.iban.trim() || null,
                bic: form.bic.trim() || null,
                account_holder: form.account_holder.trim() || null,
            });
            toast.success('Stornierungs-Anfrage eingereicht. Die Geschäftsstelle prüft den Vorgang.');
            onSubmitted?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Stornierung fehlgeschlagen');
        } finally {
            setSubmitting(false);
        }
    };

    const blocked = quote && !quote.can_self_cancel;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cancel-request-dialog">
                <DialogHeader>
                    <DialogTitle>Buchung stornieren</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
                ) : !quote ? null : (
                    <div className="space-y-4 text-sm">
                        {/* Sperre */}
                        {blocked && (
                            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-rose-50 border border-rose-200" data-testid="cancel-blocked">
                                <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />
                                <div className="text-rose-900">
                                    <strong>Storno nicht mehr möglich:</strong> Das Seminar hat bereits begonnen oder
                                    liegt in der Vergangenheit. Bitte wenden Sie sich direkt an die Geschäftsstelle.
                                </div>
                            </div>
                        )}

                        {/* Quote-Box */}
                        {!blocked && (
                            <div className={`p-4 rounded-xl border ${quote.fee_percent === 100 ? 'bg-amber-50 border-amber-200' : quote.fee_percent === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}`} data-testid="cancel-quote-box">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                                    <Wallet className="h-3.5 w-3.5" /> {quote.tier_label}
                                    {typeof quote.days_before === 'number' && (
                                        <span className="ml-auto">noch {quote.days_before} Tage bis Seminar</span>
                                    )}
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5">
                                    <span className="text-muted-foreground">Bezahlter Betrag</span>
                                    <span className="text-right font-medium">{fmt(quote.paid_amount)}</span>
                                    <span className="text-muted-foreground">Stornogebühr ({quote.fee_percent} %)</span>
                                    <span className="text-right font-medium">{fmt(quote.fee_amount)}</span>
                                    {quote.open_amount > 0 && (
                                        <>
                                            <span className="text-muted-foreground">Offene Forderung</span>
                                            <span className="text-right font-bold text-amber-700" data-testid="cancel-open-amount">{fmt(quote.open_amount)}</span>
                                        </>
                                    )}
                                    {quote.refund_amount > 0 && (
                                        <>
                                            <span className="text-muted-foreground">Erstattung an Sie</span>
                                            <span className="text-right font-bold text-emerald-700" data-testid="cancel-refund-amount">{fmt(quote.refund_amount)}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Grund */}
                        {!blocked && (
                            <>
                                <div className="space-y-1.5">
                                    <Label htmlFor="cancel-reason">Storno-Grund *</Label>
                                    <Select value={form.reason_key} onValueChange={(v) => setForm({ ...form, reason_key: v })}>
                                        <SelectTrigger id="cancel-reason" data-testid="cancel-reason-select">
                                            <SelectValue placeholder="Bitte auswählen…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {reasons.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="cancel-text">Anmerkung (optional)</Label>
                                    <textarea
                                        id="cancel-text"
                                        value={form.reason_text}
                                        onChange={(e) => setForm({ ...form, reason_text: e.target.value })}
                                        rows={2}
                                        placeholder="Falls Sie der Geschäftsstelle nähere Informationen geben möchten…"
                                        className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        data-testid="cancel-reason-text"
                                    />
                                </div>

                                {/* Bank-Details bei Erstattung */}
                                {quote.needs_bank_details && (
                                    <div className="rounded-lg border p-3 bg-slate-50 space-y-2">
                                        <div className="text-xs font-semibold flex items-center gap-1.5">
                                            <Wallet className="h-3.5 w-3.5" />
                                            Bankverbindung für die Erstattung
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <div className="sm:col-span-2 space-y-1">
                                                <Label htmlFor="cancel-holder" className="text-xs">Kontoinhaber *</Label>
                                                <Input id="cancel-holder" value={form.account_holder} onChange={e => setForm({ ...form, account_holder: e.target.value })} data-testid="cancel-holder" />
                                            </div>
                                            <div className="sm:col-span-2 space-y-1">
                                                <Label htmlFor="cancel-iban" className="text-xs">IBAN *</Label>
                                                <Input id="cancel-iban" value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })} placeholder="DE…" data-testid="cancel-iban" />
                                            </div>
                                            <div className="space-y-1">
                                                <Label htmlFor="cancel-bic" className="text-xs">BIC (optional)</Label>
                                                <Input id="cancel-bic" value={form.bic} onChange={e => setForm({ ...form, bic: e.target.value })} data-testid="cancel-bic" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.confirm}
                                        onChange={e => setForm({ ...form, confirm: e.target.checked })}
                                        className="mt-1"
                                        data-testid="cancel-confirm-check"
                                    />
                                    <span className="text-xs text-muted-foreground">
                                        Ich habe die oben angezeigte Storno-Berechnung zur Kenntnis genommen und bestätige
                                        die Stornierungs-Anfrage. Die endgültige Genehmigung erfolgt durch die Geschäftsstelle.
                                    </span>
                                </label>
                            </>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-dialog-close">
                        Abbrechen
                    </Button>
                    {!blocked && (
                        <Button
                            onClick={submit}
                            disabled={submitting || loading || !quote}
                            data-testid="cancel-submit-btn"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                            Stornierung beantragen
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
